import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { ExecutiveSummary } from '../../components/admin/KPICards';
import { 
  MonthlyEvolutionChart, 
  StatusPieChart, 
  TopReferrersChart
} from '../../components/admin/Charts';
import { TopReferrersList } from '../../components/admin/DataTables';
import { 
  RefreshCw, FileText, Calendar, 
  CheckCircle, Users, TrendingUp, ChevronRight, ArrowRight
} from 'lucide-react';
import { Button } from '../../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Embudo acumulado de las solicitudes recibidas este mes (leadsStats.funnel_month):
// cada etapa cuenta los leads que han llegado al menos hasta ella
function CommercialFunnel({ funnel }) {
  const stages = [
    {
      key: 'solicitudes',
      label: 'Solicitudes',
      value: funnel?.solicitudes || 0,
      color: 'from-blue-500 to-blue-600'
    },
    {
      key: 'contactados',
      label: 'Contactados',
      value: funnel?.contactados || 0,
      color: 'from-amber-500 to-amber-600'
    },
    {
      key: 'pruebas',
      label: 'Pruebas Agendadas',
      value: funnel?.pruebas || 0,
      color: 'from-purple-500 to-purple-600'
    },
    {
      key: 'instalados',
      label: 'Instalaciones',
      value: funnel?.instalados || 0,
      color: 'from-emerald-500 to-emerald-600'
    }
  ];

  const total = stages[0].value;

  return (
    <div className="p-6 rounded-xl bg-white/[0.02] border border-white/10">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-medium text-white font-['Outfit']">Embudo Comercial</h3>
          <p className="text-xs text-zinc-500">Solicitudes recibidas este mes</p>
        </div>
        <Link to="/admin/leads">
          <Button variant="ghost" size="sm" className="text-zinc-400 hover:text-white">
            Ver detalle <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </Link>
      </div>
      
      <div className="space-y-4">
        {stages.map((stage, index) => {
          const percentage = total > 0 ? Math.round((stage.value / total) * 100) : 0;
          const width = stage.value > 0 ? Math.max(percentage, 4) : 0; // Ancho mínimo solo si hay datos
          
          return (
            <div key={stage.key} className="relative">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-zinc-400 font-['Manrope']">{stage.label}</span>
                <span className="text-sm font-medium text-white">{stage.value}</span>
              </div>
              <div className="h-8 rounded-lg bg-white/5 overflow-hidden relative">
                {width > 0 && (
                  <div
                    className={`h-full rounded-lg bg-gradient-to-r ${stage.color} transition-all duration-500 flex items-center justify-end pr-3`}
                    style={{ width: `${width}%` }}
                  >
                    {percentage > 15 && (
                      <span className="text-xs font-medium text-white/90">{percentage}%</span>
                    )}
                  </div>
                )}
              </div>
              {index < stages.length - 1 && (
                <div className="flex justify-center my-2">
                  <ArrowRight className="w-4 h-4 text-zinc-600 rotate-90" />
                </div>
              )}
            </div>
          );
        })}
      </div>
      
      {/* Conversion rate */}
      <div className="mt-6 pt-4 border-t border-white/5">
        <div className="flex items-center justify-between">
          <span className="text-sm text-zinc-400">Tasa de conversión</span>
          <span className="text-lg font-medium text-emerald-400">
            {funnel?.conversion_rate || 0}%
          </span>
        </div>
        <p className="text-xs text-zinc-600 mt-1">Solicitudes → Instalaciones</p>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [leadsStats, setLeadsStats] = useState(null);
  const [monthly, setMonthly] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      const [statsRes, leadsRes, monthlyRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/stats`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/leads/stats`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/stats/monthly`, { withCredentials: true })
      ]);
      setStats(statsRes.data);
      setLeadsStats(leadsRes.data);
      setMonthly(monthlyRes.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchStats();
  };

  if (loading) {
    return (
      <AdminLayout
        title="Dashboard Ejecutivo"
        subtitle="Cargando estadísticas..."
        onLogout={handleLogout}
      >
        <div className="flex items-center justify-center h-96">
          <div className="w-10 h-10 border-2 border-white/20 border-t-white rounded-full animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout
      title="Dashboard Ejecutivo"
      subtitle="Vista general del sistema de referidos"
      pendingRewards={stats?.status_counts?.validado || 0}
      onLogout={handleLogout}
    >
      {/* Action Buttons */}
      <div className="flex justify-end items-center mb-4 lg:mb-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleRefresh}
          disabled={refreshing}
          className="text-zinc-400 hover:text-white hover:bg-white/10 text-xs lg:text-sm"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          <span className="hidden sm:inline">Actualizar</span>
        </Button>
      </div>

      {/* Executive Summary KPIs */}
      <div className="mb-4 lg:mb-8" data-testid="executive-summary">
        <ExecutiveSummary stats={stats} />
      </div>

      {/* New Leads Alert + Commercial Funnel */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6 mb-4 lg:mb-8">
        {/* New Leads Alert */}
        {(leadsStats?.nueva_solicitud || 0) > 0 && (
          <div className="lg:col-span-3 p-4 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center gap-4">
            <FileText className="w-6 h-6 text-blue-400 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-blue-400">
                {leadsStats?.nueva_solicitud} solicitud{leadsStats?.nueva_solicitud > 1 ? 'es' : ''} pendiente{leadsStats?.nueva_solicitud > 1 ? 's' : ''} de gestionar
              </p>
              <p className="text-xs text-zinc-400">Nuevos leads esperando contacto</p>
            </div>
            <Link to="/admin/leads">
              <Button size="sm" className="bg-blue-500 hover:bg-blue-600 text-white">
                Gestionar
              </Button>
            </Link>
          </div>
        )}
        
        {/* Commercial Funnel */}
        <div className="lg:col-span-2">
          <CommercialFunnel funnel={leadsStats?.funnel_month} />
        </div>
        
        {/* Monthly Summary */}
        <div className="p-6 rounded-xl bg-white/[0.02] border border-white/10">
          <h3 className="text-lg font-medium text-white font-['Outfit'] mb-4">Resumen del Mes</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-blue-400" />
                <span className="text-sm text-zinc-400">Solicitudes recibidas</span>
              </div>
              <span className="text-lg font-medium text-white">{leadsStats?.month_activity?.solicitudes || 0}</span>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-purple-400" />
                <span className="text-sm text-zinc-400">Pruebas agendadas</span>
              </div>
              <span className="text-lg font-medium text-white">{leadsStats?.month_activity?.pruebas_agendadas || 0}</span>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-emerald-400" />
                <span className="text-sm text-zinc-400">Instalaciones</span>
              </div>
              <span className="text-lg font-medium text-white">{leadsStats?.month_activity?.instalaciones || 0}</span>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
              <div className="flex items-center gap-3">
                <Users className="w-5 h-5 text-sky-400" />
                <span className="text-sm text-zinc-400">Clientes nuevos</span>
              </div>
              <span className="text-lg font-medium text-white">{stats?.new_clients_month || 0}</span>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
              <div className="flex items-center gap-3">
                <TrendingUp className="w-5 h-5 text-emerald-400" />
                <span className="text-sm text-emerald-300">Conversión</span>
              </div>
              <span className="text-lg font-medium text-emerald-400">{leadsStats?.funnel_month?.conversion_rate || 0}%</span>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6 mb-4 lg:mb-8">
        <MonthlyEvolutionChart data={monthly} />
        <StatusPieChart data={stats?.status_counts} />
      </div>

      {/* Bottom Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
        <div className="lg:col-span-2">
          <TopReferrersChart data={stats?.top_referrers} />
        </div>
        <TopReferrersList 
          referrers={stats?.top_referrers} 
          onViewClient={(client) => navigate(`/admin/clients?id=${client.id}`)}
        />
      </div>
    </AdminLayout>
  );
}
